// components/auth/ProtectedRoute.tsx
import React, { ReactNode } from 'react';
import { AuthService } from '../../services/authService';
import { Navigate, useLocation } from 'react-router-dom';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredUserType?: 'master' | 'grupo' | 'empresa';
  requireAnyOf?: ('master' | 'grupo' | 'empresa')[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredUserType, 
  requireAnyOf 
}) => {
  // Hooks must be called unconditionally
  const location = useLocation();
  const isAuthenticated = AuthService.isAuthenticated();
  const user = AuthService.getCurrentUser();

  // Se não está autenticado, redirecionar para login preservando a rota de origem
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Se tem requisito específico de tipo de usuário
  if (requiredUserType && user.type !== requiredUserType) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full text-center">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="mt-3">
              <h3 className="text-xs font-medium text-gray-900">
                Acesso Negado
              </h3>
              <div className="mt-2 text-xs text-gray-500">
                <p>Você não tem permissão para acessar esta área.</p>
                <p className="mt-2">
                  Tipo necessário: <strong>{requiredUserType.toUpperCase()}</strong>
                </p>
                <p>
                  Seu tipo: <strong>{user.type.toUpperCase()}</strong>
                </p>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => AuthService.logout()}
                  className="inline-flex justify-center px-2 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Fazer Login com Outra Conta
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Se tem requisito de qualquer um dos tipos especificados
  if (requireAnyOf && !requireAnyOf.includes(user.type)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full text-center">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="mt-3">
              <h3 className="text-xs font-medium text-gray-900">
                Acesso Negado
              </h3>
              <div className="mt-2 text-xs text-gray-500">
                <p>Você não tem permissão para acessar esta área.</p>
                <p className="mt-2">
                  Tipos aceitos: <strong>{requireAnyOf.map(type => type.toUpperCase()).join(', ')}</strong>
                </p>
                <p>
                  Seu tipo: <strong>{user.type.toUpperCase()}</strong>
                </p>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => AuthService.logout()}
                  className="inline-flex justify-center px-2 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Fazer Login com Outra Conta
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Se chegou aqui, usuário tem permissão
  return <>{children}</>;
};